"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Calendar, CheckCircle2, FileText, Info } from "lucide-react";
import { useState } from "react";

import { DocumentPreviewModal } from "@/components/app/documents/document-preview-modal";
import { DocumentSelectorDialog } from "@/components/app/documents/document-selector-dialog";
import { AddEventDialog } from "@/components/app/tasks/add-event-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useJourneyData } from "@/hooks/use-journey-data";
import { downloadFile } from "@/lib/download-file";
import { uploadFile } from "@/lib/storage";
import type { CalendarEventFormValues } from "@/schemas/calendar";
import type { Document, uploadSchema } from "@/schemas/document";
import type { z } from "zod";

const DOCUMENT_REF = "final_walkthrough_report";
const EVENT_REF = "final_walkthrough_event";

export function FinalWalkthroughScheduler() {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);

  const {
    documents,
    journeyDocuments,
    calendarEvents,
    journeyEvents,
    createDocument,
    addBuyerDocument,
    createEvent,
    addBuyerEvent,
    journeyData,
    deleteCalendarEvent,
    deleteBuyerEvent,
    userProfile,
  } = useJourneyData();

  // Get walkthrough document
  const walkthroughDoc = documents.find((d) =>
    journeyDocuments.some(
      (bd) => bd.document_id === d.id && bd.reference_id === DOCUMENT_REF,
    ),
  );

  // Get walkthrough event using buyerEvents
  const walkthroughBuyerEvent = journeyEvents.find(
    (be) => be.reference_id === EVENT_REF,
  );
  const walkthroughEvent = walkthroughBuyerEvent
    ? calendarEvents.find((e) => e.id === walkthroughBuyerEvent.event_id)
    : null;

  const handleScheduleEvent = async (values: CalendarEventFormValues) => {
    // Close dialog immediately for better UX
    setIsCalendarOpen(false);

    const newEvent = await createEvent.mutateAsync({
      title: "Final Walkthrough",
      type: "inspection",
      description:
        "Final property inspection before closing to verify condition and repairs",
      start_at: values.start_at.toISOString(),
      end_at: values.end_at.toISOString(),
      location: values.location,
      is_all_day: values.is_all_day,
      user_id: journeyData?.id, // Pass user_id for optimistic update
    });

    // The buyer event relationship will be created automatically through
    // the optimistic update in useJourneyData hook
  };

  const handleDeleteEvent = async () => {
    if (!walkthroughEvent?.id || !walkthroughBuyerEvent?.id) return;

    // Delete both the buyer event relationship and the calendar event
    await deleteBuyerEvent.mutateAsync(walkthroughBuyerEvent.id);
    await deleteCalendarEvent.mutateAsync(walkthroughEvent.id);
  };

  const handleUpload = async (formData: z.infer<typeof uploadSchema>) => {
    try {
      if (!userProfile?.id) throw new Error("No user ID found");

      const fileName = `${userProfile.id}/documents/${Date.now()}_${formData.file.name}`;
      const { url, path } = await uploadFile(
        "documents",
        fileName,
        formData.file,
      );

      await createDocument.mutateAsync({
        type: formData.type,
        category: formData.category,
        name: formData.file.name,
        url,
        file_path: path,
        file_type: formData.file.type,
        size_bytes: formData.file.size,
        reference_id: DOCUMENT_REF,
      });
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <Card className="p-6">
        <div className="flex flex-col md:flex-row items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold">Schedule Walkthrough</h3>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Schedule 24-48 hours before closing to allow time for
                  addressing any issues
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-sm text-muted-foreground">
              Schedule your final property inspection before closing
            </p>
          </div>

          {!walkthroughEvent ? (
            <Button
              onClick={() => setIsCalendarOpen(true)}
              disabled={createEvent.isPending || addBuyerEvent.isPending}
              className="md:px-8 md:h-11"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Schedule Walkthrough
            </Button>
          ) : (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-lg font-semibold">
                  {new Date(walkthroughEvent.start_at).toLocaleDateString(
                    undefined,
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </p>
                <p className="text-muted-foreground">
                  {new Date(walkthroughEvent.start_at).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteEvent}
                disabled={
                  deleteCalendarEvent.isPending || deleteBuyerEvent.isPending
                }
                className="hover:bg-destructive/10"
              >
                Reschedule
              </Button>
            </div>
          )}
        </div>

        <AnimatePresence>
          {walkthroughEvent && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-6 pt-6 border-t"
            >
              <div className="flex items-center gap-3 text-sm">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                </div>
                <span className="font-semibold text-primary">
                  Walkthrough scheduled
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Document Card */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold">Upload Report</h4>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  Document your findings and any issues that need addressing
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Upload your completed walkthrough report
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => setIsDocumentDialogOpen(true)}
            disabled={createDocument.isPending}
          >
            {walkthroughDoc ? "Replace Document" : "Upload Report"}
          </Button>
        </div>

        <AnimatePresence>
          {walkthroughDoc && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t"
            >
              <div className="flex items-center gap-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{walkthroughDoc.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Added{" "}
                    {new Date(walkthroughDoc.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {walkthroughDoc.file_type.startsWith("image/") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewDocument(walkthroughDoc)}
                    >
                      Preview
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      downloadFile(walkthroughDoc.url, walkthroughDoc.name)
                    }
                  >
                    Download
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      <AddEventDialog
        open={isCalendarOpen}
        onOpenChange={setIsCalendarOpen}
        onSubmit={handleScheduleEvent}
        selectedDate={new Date()}
        defaultValues={{
          title: "Final Walkthrough",
          type: "inspection",
          description:
            "Final property inspection before closing to verify condition and repairs",
          duration: 120, // 2 hours default
        }}
      />

      <DocumentSelectorDialog
        open={isDocumentDialogOpen}
        onOpenChange={setIsDocumentDialogOpen}
        title="Final Walkthrough Report"
        description="Upload your completed walkthrough report and findings"
        documents={documents}
        selectedDocument={walkthroughDoc ?? null}
        onSelect={(doc) => {
          if (doc && journeyData?.id) {
            addBuyerDocument.mutate({
              document_id: doc.id,
              reference_id: DOCUMENT_REF,
              journey_id: journeyData.id,
              user_id: userProfile?.id ?? "",
            });
          }
          setIsDocumentDialogOpen(false);
        }}
        onUpload={handleUpload}
        category="closing"
        type="final_walkthrough"
      />

      <DocumentPreviewModal
        document={previewDocument}
        onOpenChange={(open) => !open && setPreviewDocument(null)}
      />
    </div>
  );
}
